// A11yToggleBar — persistent low-vision accessibility controls.
//
// Two independent modes (both persisted in localStorage):
//   - High contrast : near-black text / stronger borders. Also auto-applied
//     when the OS requests prefers-contrast: more (tokens.css), unless the
//     user explicitly opted out via the header toggle.
//   - Large text    : ~125% typography scale.
//
// Classes land on <html> so every token-driven style picks them up.

import React, { useEffect, useState } from "react";
import { Contrast, Type } from "lucide-react";

const KEYS = {
  contrast: "wvA11yHighContrast", // "1" | "0" (explicit off) | unset (auto)
  largeText: "wvA11yLargeText", // "1" | "0"
};

const read = (key, fallback = "0") => {
  try {
    return localStorage.getItem(key) || fallback;
  } catch {
    return fallback;
  }
};

const apply = (contrastValue, largeValue) => {
  const root = document.documentElement;
  root.classList.toggle("wv-a11y-high-contrast", contrastValue === "1");
  root.classList.toggle("wv-a11y-contrast-off", contrastValue === "0");
  root.classList.toggle("wv-a11y-large-text", largeValue === "1");
};

function A11yToggleBar() {
  const [contrast, setContrast] = useState(() => read(KEYS.contrast, ""));
  const [largeText, setLargeText] = useState(() => read(KEYS.largeText));

  useEffect(() => {
    apply(contrast, largeText);
  }, [contrast, largeText]);

  useEffect(() => {
    // Sync across tabs.
    const sync = () => {
      setContrast(read(KEYS.contrast, ""));
      setLargeText(read(KEYS.largeText));
    };
    window.addEventListener("storage", sync);
    return () => window.removeEventListener("storage", sync);
  }, []);

  const toggle = (key, setter) => {
    setter((prev) => {
      const next = prev === "1" ? "0" : "1";
      try {
        localStorage.setItem(key, next);
      } catch {
        /* private mode: still applies for this session */
      }
      return next;
    });
  };

  const contrastActive = contrast === "1";

  return (
    <div className="wv-a11y-bar" role="group" aria-label="Accessibility settings">
      <button
        type="button"
        className={`wv-header__btn wv-a11y-bar__btn ${contrastActive ? "wv-a11y-bar__btn--on" : ""}`.trim()}
        aria-pressed={contrastActive}
        title="High contrast mode: stronger text and borders"
        onClick={() => toggle(KEYS.contrast, setContrast)}
      >
        <Contrast size={15} aria-hidden="true" />
        <span className="wv-a11y-bar__label">Contrast</span>
      </button>
      <button
        type="button"
        className={`wv-header__btn wv-a11y-bar__btn ${largeText === "1" ? "wv-a11y-bar__btn--on" : ""}`.trim()}
        aria-pressed={largeText === "1"}
        title="Large text mode: bigger fonts everywhere"
        onClick={() => toggle(KEYS.largeText, setLargeText)}
      >
        <Type size={15} aria-hidden="true" />
        <span className="wv-a11y-bar__label">Large&nbsp;text</span>
      </button>
    </div>
  );
}

export default A11yToggleBar;
